"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Viewer = void 0;
require("./assets/style/custom.css");
const WebglHelper_1 = require("./modules/webgl/WebglHelper");
const WebglLayer_1 = require("./modules/layers/WebglLayer");
const AnnotationLayer_1 = require("./modules/layers/AnnotationLayer");
const GuiLayer_1 = require("./modules/layers/GuiLayer");
const ProgressLayer_1 = require("./modules/layers/ProgressLayer");
const Webgl_1 = require("./modules/webgl/Webgl");
const ApiService_1 = require("./api/ApiService");
const utils_1 = require("./utils/utils");
const LayersWrapper_1 = require("./modules/layers/LayersWrapper");
const Language_1 = require("./modules/language/Language");
const DestroyLayer_1 = require("./modules/layers/DestroyLayer");
const ViewerOptions_1 = require("./objects/ViewerOptions");
const config_1 = require("./config");
const Toast_1 = require("./modules/toast/Toast");
const i18next_1 = __importDefault(require("i18next"));
const assetVersionType_1 = require("./enums/assetVersionType");
class Viewer {
    constructor() {
        this.mainFunctions = {
            resize: () => this.onResize(),
            destroy: () => this.dispose(),
            load: () => this.loadScene()
        };
    }
    /**
     * Viewer initialization
     * @param targetEl new created div element, element height is required
     * @param assetId uid of asset
     * @param options custom options
     */
    initialize(targetEl, assetId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!targetEl) {
                throw new Error('Target element is missing');
            }
            let { height } = targetEl.getBoundingClientRect();
            if (height === 0) {
                throw new Error('Height of target element is required');
            }
            if (!utils_1.Uuid.isValid(assetId)) {
                throw new Error('Asset id does not match uuid structure');
            }
            this.targetEl = targetEl;
            this.layersWrapper = new LayersWrapper_1.LayersWrapper('arc-viewer layer-wrapper');
            ViewerOptions_1.ViewerOptions.override(options);
            // TODO: do it different, call end point and check if authorized
            const hasEditor = (ViewerOptions_1.ViewerOptions.assetVersionType === assetVersionType_1.AssetVersionType.MAIN || ViewerOptions_1.ViewerOptions.assetVersionType === assetVersionType_1.AssetVersionType.PRIVATE);
            // Create html layers
            const webglLayer = new WebglLayer_1.WebglLayer('webgl-layer');
            const annotationLayer = new AnnotationLayer_1.AnnotationLayer('annotation-layer');
            this.progressLayer = new ProgressLayer_1.ProgressLayer('progress-layer');
            this.layersWrapper.container.addChild(webglLayer.container);
            this.layersWrapper.container.addChild(annotationLayer.container);
            targetEl.appendChild(this.layersWrapper.container.domEl);
            this.webgl = Webgl_1.Webgl.getInstance();
            const guiLayer = new GuiLayer_1.GuiLayer('gui-layer', hasEditor);
            this.layersWrapper.container.addChild(guiLayer.container);
            this.layersWrapper.container.addChild(this.progressLayer.container);
            const destroyEl = new DestroyLayer_1.DestroyElement(this.mainFunctions.destroy);
            this.targetEl.appendChild(destroyEl);
            this.apiService = new ApiService_1.ApiService(this.webgl, this.progressLayer, assetId, ViewerOptions_1.ViewerOptions.assetVersionType, ViewerOptions_1.ViewerOptions.tokenProvider);
            this.webglHelper = new WebglHelper_1.WebglHelper(this.webgl, this.apiService, assetId, hasEditor);
            this.language = new Language_1.Language(ViewerOptions_1.ViewerOptions.language);
            if (ViewerOptions_1.ViewerOptions.autoload) {
                this.loadScene();
            }
            this.addEvents();
        });
    }
    /**
     * Change language
     * @param language Language code ISO 639-1 (example: en, sl, ...)
     */
    changeLanguage(language) {
        var _a;
        if (!config_1.config.language.supported.find((lang) => { return lang[0] === language; })) {
            Toast_1.Toast.create('error', i18next_1.default.t('LANGUAGE_CHANGE_FAILED'), 3000);
            return;
        }
        (_a = this.language) === null || _a === void 0 ? void 0 : _a.translate(language);
    }
    setToken(tokenProvider) {
        var _a;
        (_a = this.apiService) === null || _a === void 0 ? void 0 : _a.setTokenProvider(tokenProvider);
    }
    loadScene() {
        var _a, _b;
        (_a = this.progressLayer) === null || _a === void 0 ? void 0 : _a.startLoader();
        (_b = this.apiService) === null || _b === void 0 ? void 0 : _b.getInitialObjects(ViewerOptions_1.ViewerOptions.assetVersionType); // get all asset objects from backend and show them
        this.onResize();
    }
    dispose() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            PubSub.clearAllSubscriptions();
            this.removeEvents();
            (_a = this.webglHelper) === null || _a === void 0 ? void 0 : _a.dispose();
            (_b = this.webgl) === null || _b === void 0 ? void 0 : _b.dispose();
            (_c = this.language) === null || _c === void 0 ? void 0 : _c.dispose();
            (_d = this.targetEl) === null || _d === void 0 ? void 0 : _d.remove();
            delete this.webglHelper;
            delete this.webgl;
            delete this.apiService;
            delete this.layersWrapper;
            delete this.progressLayer;
            delete this.targetEl;
        });
    }
    addEvents() {
        var _a;
        window.addEventListener('resize', this.mainFunctions.resize);
        if (!ViewerOptions_1.ViewerOptions.autoload) {
            (_a = this.progressLayer) === null || _a === void 0 ? void 0 : _a.container.setEvent('click', this.mainFunctions.load);
        }
    }
    removeEvents() {
        var _a;
        window.removeEventListener('resize', this.mainFunctions.resize);
        if (!ViewerOptions_1.ViewerOptions.autoload) {
            (_a = this.progressLayer) === null || _a === void 0 ? void 0 : _a.container.removeEvent('click', this.mainFunctions.load);
        }
    }
    onResize() {
        var _a;
        if (!this.targetEl) {
            return;
        }
        (_a = this.webgl) === null || _a === void 0 ? void 0 : _a.resize(this.targetEl);
    }
}
exports.Viewer = Viewer;
